import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { addDocument, getCollection, updateDocument, deleteDocument } from '../../services/firestoreService';

const UOM_TYPES = [
  { value: 'UNIDAD', label: 'Unidad' },
  { value: 'VOLUMEN', label: 'Volumen' },
  { value: 'MASA', label: 'Masa' },
  { value: 'EMPAQUE', label: 'Empaque' }
];

const ManageUnits = () => {
  const [units, setUnits] = useState([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [uomType, setUomType] = useState('UNIDAD');
  const [isActive, setIsActive] = useState(true);
  const [editingUnit, setEditingUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const data = await getCollection('units');
      setUnits(data);
    } catch (error) {
      console.error('Error fetching units:', error);
      setSnackbar({
        open: true,
        message: 'Error al cargar unidades de medida',
        severity: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code.trim() || !name.trim()) {
      setSnackbar({ open: true, message: 'Código y nombre son obligatorios', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      const now = new Date();
      const unitData = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        uomType,
        isActive,
        createdAt: editingUnit ? editingUnit.createdAt || now : now,
        updatedAt: now
      };

      if (editingUnit) {
        await updateDocument('units', editingUnit.id, unitData);
        setSnackbar({ open: true, message: 'Unidad actualizada exitosamente', severity: 'success' });
      } else {
        await addDocument('units', unitData);
        setSnackbar({ open: true, message: 'Unidad creada exitosamente', severity: 'success' });
      }

      handleCancelEdit();
      fetchUnits();
    } catch (error) {
      console.error('Error saving unit:', error);
      setSnackbar({ open: true, message: 'Error al guardar la unidad', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setCode(unit.code || '');
    setName(unit.name || '');
    setUomType(unit.uomType || 'UNIDAD');
    setIsActive(unit.isActive !== undefined ? unit.isActive : true);
  };

  const handleCancelEdit = () => {
    setEditingUnit(null);
    setCode('');
    setName('');
    setUomType('UNIDAD');
    setIsActive(true);
  };

  const handleDelete = async (unitId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta unidad?')) return;

    try {
      await deleteDocument('units', unitId);
      setSnackbar({ open: true, message: 'Unidad eliminada exitosamente', severity: 'success' });
      fetchUnits();
    } catch (error) {
      console.error('Error deleting unit:', error);
      setSnackbar({ open: true, message: 'Error al eliminar la unidad', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getUomTypeLabel = (value) => {
    const found = UOM_TYPES.find((t) => t.value === value);
    return found ? found.label : value;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Unidades de Medida
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Define las unidades que se utilizarán para compra, venta y dosificación (ej: TABLETA, ML, CAJA_100).
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <TextField
                fullWidth
                label="Código"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                placeholder="Ej: TAB, ML, CAJA_100"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 5
              }}>
              <TextField
                fullWidth
                label="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Ej: Tableta, Mililitro, Caja 100 tabletas"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3
              }}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={uomType}
                onChange={(e) => setUomType(e.target.value)}
                required
              >
                {UOM_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 1
              }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Activa"
              />
            </Grid>
            <Grid size={12}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ mr: 1 }}>
                {loading ? 'Guardando...' : editingUnit ? 'Actualizar Unidad' : 'Crear Unidad'}
              </Button>
              {editingUnit && (
                <Button variant="outlined" onClick={handleCancelEdit}>
                  Cancelar
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Unidades configuradas
        </Typography>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map((unit) => (
                <TableRow key={unit.id} hover>
                  <TableCell>{unit.code}</TableCell>
                  <TableCell>{unit.name}</TableCell>
                  <TableCell>{getUomTypeLabel(unit.uomType)}</TableCell>
                  <TableCell>{unit.isActive ? 'Activa' : 'Inactiva'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(unit)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(unit.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {units.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No hay unidades configuradas todavía.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUnits;
